var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import React, { Component, createRef } from 'react';
import { Key } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, eventListener, oneOf, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ReadyEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { classNames, defaultClasses, enumValues, isEmptyString, isEnumValid, isFunction } from '@philips-internal/oswald-ui-utils';
import DigitInputsAPI from './digit-inputs-api.js';
import { DIGIT_INPUTS_MAX_SIZE, DigitInputsType } from './digit-inputs-constants.js';
import { getValue } from './digit-inputs-utils.js';
import './digit-inputs.css';
const DIGIT_REGEX = /^[0-9]{0,1}$/;
const ONLY_DIGITS_REGEX = /^[0-9]+$/;
let DigitInputs = class DigitInputs extends Component {
    constructor(props) {
        super(props);
        this.onInputChange = (event, index) => {
            const { value } = event.target;
            if (!DIGIT_REGEX.test(value)) {
                return;
            }
            const callback = () => {
                this.focusNextInput(index, value);
                this.triggerReadyEvent();
            };
            this.setState((previousState) => {
                const map = previousState.valueMap;
                map.set(index, value);
                return {
                    valueMap: map,
                };
            }, callback);
        };
        this.onInputKeyDown = (event, index) => {
            const { valueMap } = this.state;
            if (!isEmptyString(valueMap.get(index)) || event.key !== Key.BACKSPACE) {
                return;
            }
            this.focusPreviousInput(index);
        };
        this.onInputPaste = (event, currentIndex) => {
            const clipboardText = event.clipboardData.getData('text') ?? '';
            const regexMatch = clipboardText.match(ONLY_DIGITS_REGEX);
            if (!regexMatch) {
                return;
            }
            const [digits] = regexMatch;
            const missingDigits = this.props.digits - currentIndex;
            const validDigits = digits.slice(0, missingDigits);
            const callback = () => {
                this.triggerReadyEvent();
                const nextIndex = currentIndex + validDigits.length - 1;
                this.forceInputFocus(nextIndex);
            };
            this.setState((previousState) => {
                const map = previousState.valueMap;
                let digitsIndex = 0;
                for (let index = currentIndex; index < this.props.digits; index++) {
                    map.set(index, validDigits[digitsIndex] ?? '');
                    digitsIndex++;
                }
                return {
                    valueMap: map,
                };
            }, callback);
        };
        this.forceInputFocus = (index) => {
            const inputRef = this.inputRefs.get(index);
            inputRef?.current?.focus();
        };
        this.focusNextInput = (index, value) => {
            if (isEmptyString(value)) {
                return;
            }
            this.forceInputFocus(index + 1);
        };
        this.focusPreviousInput = (index) => {
            this.forceInputFocus(index - 1);
        };
        const valueMap = new Map();
        const inputRefMap = new Map();
        for (let index = 0; index < DIGIT_INPUTS_MAX_SIZE; index++) {
            valueMap.set(index, '');
            inputRefMap.set(index, createRef());
        }
        this.state = {
            valueMap,
        };
        this.inputRefs = inputRefMap;
    }
    triggerReadyEvent() {
        const { digits, onReady } = this.props;
        const { valueMap } = this.state;
        const value = getValue(valueMap, digits);
        if (value.length !== digits) {
            return;
        }
        if (isFunction(onReady)) {
            onReady(new ReadyEvent({ value }));
        }
    }
    renderBoxes() {
        const { digits, enabled, readOnly } = this.props;
        const { valueMap } = this.state;
        const boxes = [];
        for (let index = 0; index < digits; index++) {
            const value = valueMap.get(index) ?? '';
            const className = classNames({
                'c-digit-inputs__box': true,
                'c-digit-inputs__box--with-value': !isEmptyString(value),
            });
            const box = (React.createElement("div", { className: className, key: `digit-inputs-${index}` },
                React.createElement("input", { className: "c-digit-inputs__input", disabled: !enabled, maxLength: 1, onChange: (event) => this.onInputChange(event, index), onKeyDown: (event) => this.onInputKeyDown(event, index), onPaste: (event) => this.onInputPaste(event, index), readOnly: readOnly, ref: this.inputRefs.get(index), value: value })));
            boxes.push(box);
        }
        return boxes;
    }
    render() {
        const { className, enabled, readOnly, type } = this.props;
        const digitInputsClassName = classNames({
            'c-digit-inputs': true,
            [`c-digit-inputs--${type}`]: isEnumValid(DigitInputsType, type),
            ...defaultClasses({
                enabled: Boolean(enabled),
                readonly: Boolean(readOnly),
            }),
            [`${className}`]: Boolean(className),
        });
        return (React.createElement("div", { className: digitInputsClassName }, this.renderBoxes()));
    }
};
DigitInputs.propTypes = {
    className: string,
    digits: oneOf([4, 5, 6, 7, 8]),
    enabled: bool,
    onReady: eventListener,
    readOnly: bool,
    type: oneOf(enumValues(DigitInputsType)),
};
DigitInputs.defaultProps = {
    digits: 6,
    enabled: true,
    readOnly: false,
    type: DigitInputsType.NUMERIC,
    value: '',
};
DigitInputs = __decorate([
    AwesomeComponent({ api: DigitInputsAPI, type: ComponentType.DATA_ENTRY }),
    __metadata("design:paramtypes", [Object])
], DigitInputs);
export default DigitInputs;
//# sourceMappingURL=digit-inputs.js.map